import { ChipSelect } from '@/dsm-components';
import { useGetDesignToolsQuery } from '@/services/designTools';
import { useCallback, useMemo } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { DialogFormValues } from './checklistItemInputOptions';
import { serializeParamsRaw } from './paramsRaw';

interface DesignPlatformParamAdderProps {
  fieldPrefix: `conditions` | `inputs.${number}.conditions`;
  currentParams: string[];
  conditionIndex: number;
  setValue: UseFormSetValue<DialogFormValues>;
}

export const DesignPlatformParamAdder = ({
  fieldPrefix,
  currentParams,
  conditionIndex,
  setValue,
}: DesignPlatformParamAdderProps) => {
  const { data: designTools, isLoading } = useGetDesignToolsQuery();

  const availableOptions = useMemo(() => {
    if (!designTools) return [];
    return Object.keys(designTools)
      .filter((key) => !currentParams.includes(key))
      .map((key) => ({ value: key, label: designTools[key] }))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [designTools, currentParams]);

  const handleChange = useCallback(
    (newValue: string) => {
      if (newValue && !currentParams.includes(newValue)) {
        const newParams = [...currentParams, newValue];
        setValue(`${fieldPrefix}.${conditionIndex}.paramsRaw`, serializeParamsRaw(newParams));
      }
    },
    [fieldPrefix, currentParams, conditionIndex, setValue],
  );

  if (isLoading) {
    return <span className="text-xs text-text-tertiary">Loading...</span>;
  }

  if (availableOptions.length === 0) {
    return null;
  }

  return (
    <ChipSelect
      searchable
      options={availableOptions}
      value=""
      onChange={handleChange}
      placeholder="+ design platform"
    />
  );
};
